<template>
    <div class="w-100 user-modal-register-view px-2">
        <div class="mb-5 text-center">
            <p class="font-weight-bold font-size-200 mb-0">{{ 'Create your account' | trans }}</p>
        </div>

        <SocialSignupButtons
            class="mb-4 w-100 text-center"
            :reload-page="false"
            @auth-success="onSocialAuthSuccess"
        ></SocialSignupButtons>

        <div class="mb-4 text-center py-2">
            <strong>{{ 'Or sign up using your e-mail address' | trans }}:</strong>
        </div>

        <div class="mb-5" @keyup.enter="attemptRegister">
            <TextInput
                data-user-modal-input-email
                v-model="emailValue"
                class="mb-4"
                type="email"
                name="u_email"
                :label="'E-Mail' | trans"
                icon="far fa-envelope"
                @input="updateEmail"
                :disabled="isSubmitting"
                :has-error="emailError !== null"
                :current-error="emailError"
                :trim-input="true"
                autocomplete="email"
            ></TextInput>

            <TextInput
                data-user-modal-input-password
                type="password"
                name="u_password"
                :label="'Password' | trans"
                icon="far fa-lock"
                @input="updatePassword"
                :disabled="isSubmitting"
                :has-error="passwordError !== null"
                :current-error="passwordError"
                :trim-input="false"
                autocomplete="new-password"
            >
                <template v-slot:after-input>
                    <PasswordStrengthMeter
                        :password="passwordValue"
                        class="pull-right pr-4 pt-2 pb-3 pl-2"
                    ></PasswordStrengthMeter>
                </template>
                <template v-slot:after-error>
                    <!-- min-height, w-100 & d-block needed as workaround for a very particular cornercase -->
                    <span class="clearfix w-100 d-block" style="min-height: 1px"></span>
                </template>
            </TextInput>

            <SimpleCheckbox
                data-user-modal-input-tos
                class="mt-4 card p-4"
                :class="{ 'vuetiful-input-error': tosError !== null }"
                name="u_tos"
                align="left"
                @input="updateTosValue"
                :disabled="isSubmitting"
                :has-error="tosError !== null"
                :current-error="tosError"
            >
                <span v-html="tosText" class="tos-links-bold"></span>
            </SimpleCheckbox>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-4"
            icon="fa fa-user-plus"
            :text="'Sign up' | trans"
            :is-state-loading="isSubmitting"
            @click.stop.prevent="attemptRegister"
        ></ButtonWithLoadingState>

        <div class="text-center">
            {{ 'Having trouble?' | trans }}
            <a data-user-modal-link-help href="" @click.stop.prevent="goToHelpPage">{{ 'Get help' | trans }}</a>
        </div>

        <hr class="mb-4 mt-4" />

        <div class="text-center">
            {{ 'Already have an account?' | trans }}
            <RouterLink
                data-user-modal-link-login
                :to="{ name: userModalRoute.LOGIN }"
                :disabled="isSubmitting"
                :event="isSubmitting ? '' : 'click'"
                tag="a"
                >{{ 'Log in' | trans }}
            </RouterLink>
        </div>
    </div>
</template>

<script>
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import TextInput from '../../../../../qaamgo/modules/sat/components/form/TextInput';
import {
    testIsEmptyString,
    testIsNotNull,
    testIsNumber,
    testIsTrimmedStringEmpty,
} from '../../../../../lib/test-and-assert/test-base';
import { translate } from '../../../../../qaamgo/helper/translate';
import { pageStoreMixin } from '../../../../store/page-store';
import { User } from '../../../user';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';
import { testIsEmailValid } from '../../../test';
import SimpleCheckbox from '../../../../../qaamgo/modules/sat/components/form/SimpleCheckbox';
import { translatePath } from '../../../../../qaamgo/sat/helper/translate-path';
import PasswordStrengthMeter from '../../../../../qaamgo/modules/sat/components/form/PasswordStrengthMeter';
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { userModalRoute } from '../router';
import { userManager } from '../../../user-manager';
import { REGISTER_FAIL_INVALID_EMAIL } from '../../../user-api';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';
import SocialSignupButtons from '../components/SocialSignupButtons';
import { paymentApi } from '../../../../payment/payment-api';
import { paymentManager } from '../../../../payment/payment-manager';
import { projectHelper } from '../../../../misc/project-helper';

const errorMessages = {
    emailInvalid: translate('E-Mail is not valid'),
    emailEmpty: translate('Enter your e-mail address'),
    passwordEmpty: translate('Password can not be empty'),
    passwordTooShort: translate('Password must be at least %amount% characters long', {
      '%amount%': 8
    }),
    passwordTooLong: translate('Password can not be greater than %amount% characters long', {
      '%amount%': 256
    }),
    tosEmpty: translate('You need to accept the TOS.'),
};

export default {
    name: 'RegisterView',
    components: { SocialSignupButtons, PasswordStrengthMeter, SimpleCheckbox, TextInput, ButtonWithLoadingState },
    mixins: [TransFilter, pageStoreMixin],
    created() {
        const emailFromStore = this.store.userPaymentModal.email;
        if (testIsNotNull(emailFromStore)) {
            this.updateEmail(emailFromStore);
        }
    },
    computed: {
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
        /** @return {User} */
        storeUser() {
            return this.store.user.getUser;
        },
        /** @return {Boolean} */
        skipPayment() {
            return this.store.userPaymentModal.skipPayment;
        },
        /** @return {Boolean} */
        skipPlanSelect() {
            return this.store.userPaymentModal.skipPlanSelect;
        },
        /** @return {Boolean} */
        selectedProduct() {
            return this.store.userPaymentModal.selectedProduct;
        },
        shouldShowEducational() {
            return (
                !projectHelper.isApi2Convert() &&
                !this.storeUser.hasRole(User.ROLE_USER_EDUCATIONAL) &&
                this.storeUser.hasRole(User.ROLE_USER_ELIGIBLE_FOR_EDUCATIONAL)
            );
        },
        tosText: function () {
            let translated = translate(
                `I accept the <a href=\"%termsOfUseLink%\" target=\"_blank\">Terms of Use</a> and the <a href=\"%privacyPolicyLink%\" target=\"_blank\">Privacy Policy</a>`
            );

            return translated
                .replace('%termsOfUseLink%', translatePath('/terms'))
                .replace('%privacyPolicyLink%', translatePath('/privacy'));
        },
    },
    methods: {
        goToHelpPage() {
            const link = satGlobals.getMainUrl() + translatePath('/help') + '#newAccount';

            window.open(link, '_blank');
        },
        updateEmail(value) {
            this.emailValue = value;

            this.emailError = null;
        },
        updatePassword(value) {
            this.passwordValue = value;

            this.passwordError = null;
        },
        updateTosValue(value) {
            this.hasAcceptedTos = value;

            this.tosError = null;
        },
        validateEmailAndSetErrors() {
            if (testIsTrimmedStringEmpty(this.emailValue)) {
                this.emailError = errorMessages.emailEmpty;

                return;
            }

            if (!testIsEmailValid(this.emailValue)) {
                this.emailError = errorMessages.emailInvalid;

                return;
            }

            this.emailError = null;
        },
        validatePasswordAndSetErrors() {
            if (testIsEmptyString(this.passwordValue)) {
                this.passwordError = errorMessages.passwordEmpty;

                return;
            }

            if (this.passwordValue.length < 8) {
                this.passwordError = errorMessages.passwordTooShort;

                return;
            }
            if (this.passwordValue.length > 256) {
                this.passwordError = errorMessages.passwordTooLong;

                return;
            }

            this.passwordError = null;
        },
        async attemptRegister() {
            this.validateEmailAndSetErrors();
            this.validatePasswordAndSetErrors();

            if (!this.hasAcceptedTos) {
                this.tosError = errorMessages.tosEmpty;

                return;
            }

            if (testIsNotNull(this.emailError) || testIsNotNull(this.passwordError)) {
                return;
            }

            globalLogger.log('user modal - register attempt - email - start');
            this.store.userPaymentModal.setSubmitting(true);

            let user = new User();

            user.setEmail(this.emailValue).setPassword(this.passwordValue).setLanguage(satGlobals.getLocale());

            try {
                const registerResponse = await userManager.registerUser(user);

                user = registerResponse?.user;

                globalLogger.log('user modal - register attempt - email - success');

                const offerTrial = satGlobals.getEnableFreeTrial() && user.isEligibleForFreeTrial();
                this.store.userPaymentModal.setOfferTrial(offerTrial);

                if (!paymentManager.isPricingInfoInitialized()) {
                    const pricingInformation = await paymentApi.getPricingInformation(true);

                    paymentManager.setConfigFromPricingInformation(pricingInformation);
                }

                this.onRegisterSuccess();
            } catch (error) {
                globalLogger.log('user modal - register attempt - email - fail', error);

                this.store.userPaymentModal.setSubmitting(false);

                if (error === REGISTER_FAIL_INVALID_EMAIL) {
                    this.emailError = errorMessages.emailInvalid;

                    return;
                }

                pageNotification.showGenericErrorMessage();
            }
        },
        async onSocialAuthSuccess() {
            const offerTrial = satGlobals.getEnableFreeTrial() && this.storeUser.isEligibleForFreeTrial();
            this.store.userPaymentModal.setOfferTrial(offerTrial);

            if (!paymentManager.isPricingInfoInitialized()) {
                const pricingInformation = await paymentApi.getPricingInformation(true);

                paymentManager.setConfigFromPricingInformation(pricingInformation);
            }

            this.onRegisterSuccess();
        },
        onRegisterSuccess() {
            if (this.shouldShowEducational) {
                // eligible edu user signed up
                // => skip all payment steps & account created step
                this.$router.push({ name: userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT });

                return;
            }

            if (this.skipPayment) {
                // user clicked on 'free' on the pricing page
                // => skip all payment steps
                this.$router.push({ name: userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT });

                return;
            }

            if (projectHelper.isApi2Convert()) {
                this.$router.push({
                    name: userModalRoute.REGISTER_PLAN_SELECT_CONVERSION_MINUTES,
                    params: {
                        isNewAccount: true,
                    },
                });

                return;
            }

            if (this.skipPlanSelect) {
                this.$router.push({ name: userModalRoute.REGISTER_CHOOSE_BILLING });

                return;
            }

            if (satGlobals.isMultipleProductsEnabled()) {
                this.$router.push({
                    name: userModalRoute.REGISTER_PLAN_CREDITS_SELECT,
                    params: {
                        isNewAccount: true,
                    },
                });

                return;
            }

            if (satGlobals.isCreditsInAllSatsEnabled()) {
                // if there is a selectedProduct, that means it comes from the payment modal
                // otherwise, it comes from 'go premium' button
                if (this.selectedProduct) {
                    this.$router.push({
                        name: userModalRoute.REGISTER_CHOOSE_PAYMENT,
                        params: {
                            isNewAccount: true,
                        },
                    });

                    return;
                }

                this.$router.push({
                    name: userModalRoute.REGISTER_PRODUCT_CREDITS_SELECT,
                    params: {
                        isNewAccount: true,
                    },
                });
                
                return;
            }

            this.$router.push({ name: userModalRoute.REGISTER_PLAN_SELECT });
        },
    },
    data() {
        return {
            userModalRoute: userModalRoute,
            emailValue: '',
            emailError: null,
            passwordValue: '',
            passwordError: null,
            hasAcceptedTos: false,
            tosError: null,
        };
    },
};
</script>

<style lang="scss">
.tos-links-bold a {
    font-weight: bold;
}
</style>
